import { useQueryClient } from '@tanstack/react-query'
import { AppQueryOptions, useAppQueryMap } from 'hooks/use-app-query'
import { getLeadContactApi } from 'services/lead-contact'
import { GetLeadContactsResponse } from 'services/types/lead-contact'
import { LeadDetails } from 'types/global'
import { STALE_TIME } from 'utils/constants'
import { leadContactKey } from 'utils/constants/queryKey'
import { formatContactNumber, formatContactNumberDisplayValue } from 'utils/generic'

const useLeadContact = (id: string, options?: AppQueryOptions) =>
  useAppQueryMap(
    leadContactKey.list({
      lead_id: id,
    }),
    () => getLeadContactApi({ lead_id: id }),
    mapGetLeadContactsResponse,
    {
      staleTime: STALE_TIME.minute,
      ...options,
    },
  )

export const useLeadContactQueryClient = (id: string) => {
  const queryClient = useQueryClient()
  const invalidateQuery = () => queryClient.invalidateQueries(leadContactKey.list({ lead_id: id }))
  return {
    invalidateQuery,
  }
}

export const mapGetLeadContactsResponse: (data: GetLeadContactsResponse[]) => LeadDetails.ContactPerson[] = (result) =>
  result.map((leadContact: GetLeadContactsResponse) => ({
    id: leadContact.contact_id,
    contactTypeId: leadContact.contact_type_id,
    email: leadContact.email.map((email) => ({
      id: email.contact_info_id,
      contactInfoTypeId: email.contact_info_type_id,
      isPrimary: email.is_primary,
      value: email.value,
    })),
    fname: leadContact.fname,
    jobTitle: leadContact.job_title,
    leadId: leadContact.lead_id,
    isActive: leadContact.is_active,
    isPrimary: leadContact.is_primary,
    lname: leadContact.lname,
    mobile: leadContact.mobile.map((mobile) => ({
      id: mobile.contact_info_id,
      contactInfoTypeId: mobile.contact_info_type_id,
      contactInfoType: mobile.contact_info_type,
      isPrimary: mobile.is_primary,
      countryCode: Number(mobile.prefix),
      countryISO: mobile.region,
      number: mobile.value,
      displayValue: formatContactNumberDisplayValue(Number(mobile.prefix), mobile.value),
      value: formatContactNumber(Number(mobile.prefix), mobile.value),
      lastCallTimestamp: mobile.last_call_timestamp,
      lastCallStatus: mobile.last_call_status,
      lastCallOutcome: mobile.last_call_outcome,
    })),
  }))

export default useLeadContact
